import type {
  ImageReference, PrinterProfile, ResolvedImage, ResourceContract, Template,
} from '@tsagko/label-contracts'
import { err, ok, type Result } from '@tsagko/label-contracts'
import { flattenZones } from '@tsagko/label-zpl-pipeline'
import { analyzeBitmap, decodeImage, rasterizeImage, type ImageAnalysis } from '../../canvas/rasterize-image'

export interface ImageJob {
  componentId: string
  source: ImageReference
  fit: 'contain' | 'cover' | 'stretch'
  threshold: number | undefined
  regionWidthDots: number
  regionHeightDots: number
}

const mmToDots = (mm: number, dpi: number) => Math.round((mm * dpi) / 25.4)

/** Pure: walk a template and emit one rasterization job per image leaf. */
export function collectImageJobs(template: Template, dpi: number): ImageJob[] {
  const jobs: ImageJob[] = []
  for (const z of flattenZones(template)) {
    if (z.component.type !== 'image') continue
    jobs.push({
      componentId: z.component.id,
      source: z.component.source,
      fit: z.component.fit ?? 'contain',
      threshold: z.component.threshold,
      regionWidthDots: mmToDots(z.widthMm, dpi),
      regionHeightDots: mmToDots(z.heightMm, dpi),
    })
  }
  return jobs
}

/** Resolve a reference to its encoded bytes + mime. Never throws. */
export async function resolveImageBytes(
  ref: ImageReference,
  rc: ResourceContract,
): Promise<Result<{ bytes: Uint8Array; mimeType: string }>> {
  try {
    if (ref.kind === 'data-uri') {
      // Gate: an empty data-uri value means the user hasn't chosen a file yet.
      // Skip silently (no console noise) rather than emitting an IMAGE_UNRESOLVED
      // warning for an expected transient state.
      if (!ref.value) return err('IMAGE_UNRESOLVED', 'Empty data URI — no file chosen yet')
      const m = /^data:([^;,]+)?(;base64)?,(.*)$/s.exec(ref.value)
      if (!m) return err('IMAGE_UNRESOLVED', 'Malformed data URI')
      const mimeType = m[1] ?? 'application/octet-stream'
      const bytes = m[2]
        ? Uint8Array.from(atob(m[3]!), c => c.charCodeAt(0))
        : new TextEncoder().encode(decodeURIComponent(m[3]!))
      return ok({ bytes, mimeType })
    }
    if (ref.kind === 'url') {
      const res = await fetch(ref.url)
      if (!res.ok) return err('IMAGE_UNRESOLVED', `Fetch ${ref.url} -> ${res.status}`)
      const buf = new Uint8Array(await res.arrayBuffer())
      return ok({ bytes: buf, mimeType: res.headers.get('content-type') ?? 'image/png' })
    }
    if (ref.kind === 'host-asset') {
      const loaded = await rc.loadImageAsset(ref.assetId)
      if (loaded.status !== 'ok') return err('IMAGE_UNRESOLVED', `Asset ${ref.assetId} not found`)
      return ok({ bytes: loaded.data.resource.data, mimeType: loaded.data.resource.mimeType })
    }
    // 'field' kind is not resolvable at this layer (requires runtime data binding)
    return err('IMAGE_UNRESOLVED', `Unsupported image source kind: ${(ref as { kind: string }).kind}`)
  } catch (e) {
    return err('IMAGE_UNRESOLVED', e instanceof Error ? e.message : 'resolve failed')
  }
}

/**
 * Returns true when an image source is intentionally empty (no file chosen
 * yet, no URL typed). Used to suppress console.warn noise for the expected
 * transient state where a user has selected the Image primitive type but
 * hasn't picked a source yet.
 */
function isEmptyImageSource(source: ImageReference): boolean {
  if (source.kind === 'data-uri') return !source.value
  if (source.kind === 'url') return !source.url
  if (source.kind === 'host-asset') return !source.assetId
  return false
}

/**
 * Build the component-id-keyed ResolvedImage map for a template. Unresolved
 * images are skipped (no throw) — render-plan falls back to the placeholder.
 * Empty sources (no file/URL chosen yet) are skipped silently (no console noise).
 */
export async function resolveImagesForTemplate(
  template: Template,
  profile: PrinterProfile,
  rc: ResourceContract,
): Promise<Map<string, ResolvedImage>> {
  const map = new Map<string, ResolvedImage>()
  for (const job of collectImageJobs(template, profile.dpi)) {
    // Skip silently for empty sources — the user hasn't chosen a file/URL yet.
    if (isEmptyImageSource(job.source)) continue
    const bytes = await resolveImageBytes(job.source, rc)
    if (bytes.status !== 'ok') {
      // eslint-disable-next-line no-console
      console.warn('[resolveImages] unresolved', job.componentId, bytes.error)
      continue
    }
    try {
      const resolved = await rasterizeImage(
        bytes.data.bytes, bytes.data.mimeType,
        job.regionWidthDots, job.regionHeightDots, job.fit, job.threshold,
      )
      map.set(job.componentId, resolved)
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[resolveImages] rasterize failed', job.componentId, e)
    }
  }
  return map
}

/**
 * Resolve a source reference to its luminance analysis for the property panel.
 * Canvas glue (decode + analyze); returns null on any failure or missing adapter.
 */
export async function analyzeImageSource(
  source: ImageReference,
  rc: ResourceContract | undefined,
): Promise<ImageAnalysis | null> {
  if (!rc) return null
  const bytes = await resolveImageBytes(source, rc)
  if (bytes.status !== 'ok') return null
  try {
    const { bitmap } = await decodeImage(bytes.data.bytes, bytes.data.mimeType)
    return analyzeBitmap(bitmap)
  } catch {
    return null
  }
}
